/**
 * Hook minimo para cargar datos de la API.
 *
 * Por que no TanStack Query / SWR: este scaffold existe para probar el stack de
 * punta a punta con la menor superficie posible. Todavia no hay cache
 * compartida, ni invalidacion, ni mutaciones optimistas que justifiquen la
 * dependencia. Cuando las pantallas se multipliquen, este hook es exactamente
 * lo que se reemplaza; hasta entonces, 60 lineas legibles ganan.
 *
 * Lo que si resuelve, porque es donde se rompen las versiones caseras:
 *
 * - **Cancelacion**: cada carga lleva su `AbortController` y el efecto lo
 *   aborta al desmontar o al cambiar las dependencias.
 * - **Respuestas fuera de orden**: si el usuario cambia de tienda dos veces
 *   rapido, la respuesta lenta de la primera no puede pisar a la segunda. Se
 *   descarta comparando contra el request vigente.
 */

import { useCallback, useEffect, useRef, useState } from "react";

export type AsyncState<T> =
  | { readonly kind: "idle" }
  | { readonly kind: "loading" }
  | { readonly kind: "error"; readonly error: unknown }
  | { readonly kind: "ready"; readonly data: T };

export interface AsyncResult<T> {
  readonly state: AsyncState<T>;
  /** Volver a ejecutar la carga (boton "reintentar"). */
  readonly reload: () => void;
}

/**
 * Ejecutar `loader` cuando cambien `deps` y exponer su estado.
 *
 * @param loader Funcion que recibe un `AbortSignal` y devuelve los datos.
 * @param deps Dependencias que disparan una recarga.
 * @param enabled Si es `false`, no se carga nada y el estado queda en `idle`.
 *   Sirve para "todavia no se eligio tienda".
 */
export function useAsync<T>(
  loader: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
  enabled = true,
): AsyncResult<T> {
  const [state, setState] = useState<AsyncState<T>>({ kind: "idle" });
  const [nonce, setNonce] = useState(0);

  // El loader cambia de identidad en cada render (es una arrow function en el
  // componente). Guardarlo en una ref evita que el efecto se dispare en loop
  // sin obligar a cada llamador a envolverlo en useCallback.
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  /** Identifica al request vigente; descarta los que llegan tarde. */
  const requestId = useRef(0);

  useEffect(() => {
    if (!enabled) {
      setState({ kind: "idle" });
      return;
    }

    const controller = new AbortController();
    requestId.current += 1;
    const currentRequest = requestId.current;

    setState({ kind: "loading" });

    loaderRef
      .current(controller.signal)
      .then((data) => {
        if (currentRequest !== requestId.current) return;
        setState({ kind: "ready", data });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        if (currentRequest !== requestId.current) return;
        setState({ kind: "error", error });
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, nonce, ...deps]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  return { state, reload };
}
